import { TermRequest } from "@/pages/financial/term-validation/data/types";
import FileViewer from "@/pages/financial/term-validation/view/components/file-viewer";
import {
  useGetFilesQuery,
  useSignedUrlMutation,
  useUploadFileMutation,
} from "@/services/file";
import {
  useGetTermByIdQuery,
  useGetTermHistoryQuery,
  useGetTermPdfQuery,
  useUpdateTermStatusMutation,
} from "@/services/generate-term/generate-term";
import { formatToBRL } from "@/utils/formatToMoney";
import { Load } from "components/load";
import PageError from "components/page-error";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import { ChevronLeft, Download, Loader2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HistoryTimeline } from "./components/timeline";

const REASON_TRANSLATIONS: Record<string, string> = {
  TROCA_GERENTE: "Troca de Gerente",
  FERIAS: "Férias",
  ATESTADO: "Atestado",
  DESLIGAMENTO: "Desligamento",
  LICENCA_MATERNIDADE: "Licença Maternidade",
  PEDIDO_DEMISSAO: "Pedido de Demissão",
  OUTROS: "Outros",
};

export const getReasonTranslation = (reason: string): string =>
  REASON_TRANSLATIONS[reason] ?? reason;

export const ViewTerm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [updateTermStatus] = useUpdateTermStatusMutation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadFile] = useUploadFileMutation();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [signedUrl] = useSignedUrlMutation();
  const { data: filesData, refetch: refetchFiles } = useGetFilesQuery({
    entity_name: "TERM_REQUEST",
    id: id || "",
  });

  const {
    data: term,
    isLoading: isLoadingTerm,
    error: termError,
    refetch: refetchTerm,
  } = useGetTermByIdQuery(id || "", {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: history,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useGetTermHistoryQuery(id || "", {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: pdfContent,
    isLoading: isLoadingPdf,
    error: pdfError,
  } = useGetTermPdfQuery(id || "", { skip: !id });

  const isLoading = isLoadingTerm || isLoadingHistory || isLoadingPdf;
  const hasError = termError || historyError || pdfError;

  useEffect(() => {
    if (id) {
      refetchHistory();
    }
  }, [id, refetchHistory]);

  useEffect(() => {
    if (term?.status === "PENDING") {
      setFileUrl(null);
      return;
    }

    if (filesData?.data?.[0]?.id) {
      signedUrl({
        id: filesData?.data[0].id,
        ResponseContentDisposition: "inline",
      })
        .then((response) => {
          if (response?.data?.url) {
            setFileUrl(response.data.url);
            console.log(response.data.url);
          }
        })
        .catch((error) => {
          console.error("Erro ao obter a URL do arquivo", error);
        });
    }
  }, [signedUrl, filesData, term?.status]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-500 text-white";
      case "REJECTED":
        return "bg-red-500 text-white";
      case "PENDING":
        return "bg-yellow-500 text-white";
      default:
        return "bg-gray-500 text-white";
      case "TERM_SIGNED":
        return "bg-blue-500 text-white";
      case "TERM_PENDING_SIGNATURE":
        return "bg-orange-500 text-white";
    }
  };

  const getStatusTranslation = (status: string) => {
    switch (status) {
      case "APPROVED":
        return "Aprovado";
      case "REJECTED":
        return "Rejeitado";
      case "PENDING":
        return "Pendente";
      case "TERM_SIGNED":
        return "Assinado";
      case "TERM_PENDING_SIGNATURE":
        return "Assinatura Pendente";
      case "CANCELED":
        return "Cancelado";
      default:
        return status;
    }
  };

  if (isLoading) return <Load />;
  if (hasError || !term) {
    return (
      <PageError
        status={500}
        message="Ocorreu um erro interno. Por favor, tente novamente mais tarde."
      />
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleUpload(selectedFile);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setIsUploading(true);
      await uploadFile({
        file,
        entity_name: "TERM_REQUEST",
        entity_id: id,
      }).unwrap();

      toast({
        variant: "default",
        description: "Termo enviado com sucesso",
        duration: 5000,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await updateTermStatus({
        termId: id!,
        status: "TERM_SIGNED",
        comment: "Termo assinado",
      });

      refetchTerm();
      refetchHistory();
      refetchFiles();
    } catch (error) {
      console.error("Failed to upload file:", error);
      toast({
        variant: "destructive",
        description: "Erro ao realizar ação",
        duration: 5000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const files = filesData?.data;
      if (files && files.length > 0) {
        const response = await signedUrl({ id: files[0].id }).unwrap();
        if (response?.url) {
          window.open(response.url, "_blank");
        }
        toast({
          variant: "default",
          description: "Arquivo baixado com sucesso",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Erro ao tentar obter a URL para download:", error);
      toast({
        description: "Erro ao tentar baixar o arquivo. Tente novamente.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6 md:px-6 md:py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-full h-10 w-10 p-0 shadow-sm hover:shadow transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-800">
              Visualizar Termo
            </h2>
          </div>

          <Badge
            className={`${getStatusBadgeVariant(
              term.status,
            )} px-3 py-1 text-sm font-medium rounded-full shadow-sm`}
            style={{ pointerEvents: "none" }}
          >
            {getStatusTranslation(term.status)}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden border-none shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="bg-gray-50 border-b pb-3">
                <CardTitle className="text-xl text-gray-800">
                  Informações do Termo
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Nome do Usuário
                    </Label>
                    <Input
                      value={term.user_name}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Saldo em Caixa
                    </Label>
                    <Input
                      value={formatToBRL(term.cash_balance)}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Data da Auditoria
                    </Label>
                    <Input
                      value={formatDate(term.treasury_audit_date)}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Motivo
                    </Label>
                    <Input
                      value={getReasonTranslation(term.reason)}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Loja
                    </Label>
                    <Input
                      value={term.store_code}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-600">
                      CPF
                    </Label>
                    <Input
                      value={term.cpf ? formatCpf(term.cpf) : "Não informado"}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-sm font-medium text-gray-600">
                      Justificativa
                    </Label>
                    <Textarea
                      value={term.justification || "Sem justificativa"}
                      readOnly
                      className="bg-gray-50 border-gray-200 focus-visible:ring-primary/20 min-h-[100px] resize-none"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {((term.status as TermRequest["status"]) ===
              "TERM_PENDING_SIGNATURE" ||
              (term.status as TermRequest["status"]) === "APPROVED" ||
              (term.status as TermRequest["status"]) === "TERM_SIGNED") && (
              <Card className="overflow-hidden border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="flex flex-row items-center justify-between bg-gray-50 border-b pb-3">
                  <CardTitle className="text-xl text-gray-800">
                    Visualização do Termo
                  </CardTitle>
                  {pdfContent && (
                    <div className="space-x-2 flex items-center">
                      {(term.status as TermRequest["status"]) ===
                        "TERM_PENDING_SIGNATURE" && (
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="hidden"
                          ref={fileInputRef}
                        />
                      )}
                      {(term.status as TermRequest["status"]) ===
                        "TERM_PENDING_SIGNATURE" && (
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          variant="outline"
                          size="sm"
                          className="h-8 flex items-center gap-1.5 shadow-sm hover:shadow transition-all"
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              <span>Enviar Termo</span>
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          if (
                            (term.status as TermRequest["status"]) ===
                              "PENDING" ||
                            (term.status as TermRequest["status"]) ===
                              "REJECTED" ||
                            (term.status as TermRequest["status"]) ===
                              "TERM_PENDING_SIGNATURE"
                          ) {
                            const link = document.createElement("a");
                            link.href = `data:application/pdf;base64,${pdfContent}`;
                            link.download = `termo-${term.user_name}.pdf`;
                            link.click();
                          } else {
                            handleDownload();
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="h-8 flex items-center gap-1.5 shadow-sm hover:shadow transition-all"
                      >
                        <Download className="h-4 w-4" />
                        <span>Baixar Termo</span>
                      </Button>
                    </div>
                  )}
                </CardHeader>
                {(term.status as TermRequest["status"]) === "PENDING" ||
                (term.status as TermRequest["status"]) === "REJECTED" ||
                (term.status as TermRequest["status"]) ===
                  "TERM_PENDING_SIGNATURE" ? (
                  <div className="p-4 bg-gray-100 rounded-b-lg">
                    <iframe
                      src={`data:application/pdf;base64,${pdfContent}#toolbar=0&view=FitH`}
                      className="w-full h-[600px] border rounded-md shadow-sm bg-white"
                    />
                  </div>
                ) : term.status === "APPROVED" ||
                  (term.status as TermRequest["status"]) === "TERM_SIGNED" ? (
                  <FileViewer fileUrl={fileUrl!} />
                ) : null}
              </Card>
            )}
          </div>

          <div className="lg:col-span-1">
            <Card className="h-full border-none shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="bg-gray-50 border-b pb-3">
                <CardTitle className="text-xl text-gray-800">
                  Histórico de Solicitação
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <HistoryTimeline history={history} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatDate = (date: Date) => {
  return format(new Date(date), "dd/MM/yyyy");
};

const formatCpf = (cpf: string) => {
  const numericCpf = cpf.replace(/\D/g, "");

  if (numericCpf.length === 11) {
    return numericCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  return cpf;
};
